<script>
import {computed, ref, watch} from "vue";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useTemplateApplicationStore} from "@/stores/Edms/TemplateApplication";
import StrTemplateAutocomplete from "@/views/Edms/Template/Components/StrTemplateAutocomplete.vue";
import useValidation from "@/use/Validation";
import axios from "axios";
import StrQuillEditorCorePdf from "@/components/StrQuillEditorCorePdf.vue";

export default {
    name: "ListingModel",
    components: {StrQuillEditorCorePdf, StrTemplateAutocomplete},
    props: {
        module: String
    },
    setup(props) {
        const {
            loading,
            overlay,
            filter,
            total,
            itemsPerPage,
        } = useSharedData();
        const {mxRules} = useValidation();
        const {getBack, itemClick, rowColor, transformFilterValues} = useSharedMethod();
        const {$route, $i18n, $refs} = useTools();

        const templateApplicationStore = useTemplateApplicationStore();


        const headers = [
            {
                text: "...",
                align: "left",
                value: "action",
                sortable: false,
                width: "0%"
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                value: "name",
                sortable: true,
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    modifier(item)
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    supprimer(item)
                },
            },
            {
                title: $i18n.t("common.print"),
                icon: 'mdi-printer',
                color: 'blue-grey',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    print(item);
                },
            },
        ];

        const searchParams = ref({
            sortBy: "name",
            descending: false,
            page: 1,
            itemsPerPage: itemsPerPage,
        });

        const listingLoading = ref(false);

        function handleSearch(params) {
            listingLoading.value = true;
            templateApplicationStore.setTemplateApplications([]);
            total.value = 0;

            filter.value.moduleId = $route.params.id;
            filter.value.module = props.module;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/templateApplications', {params: transformFilterValues(filter.value)}).then(response => {
                templateApplicationStore.setTemplateApplications(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                listingLoading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});


        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        const dialog = ref(false);
        const addAction = ref(false);
        const updateAction = ref(false);
        const deleteAction = ref(false);
        const viewAction = ref(false);
        const printAction = ref(false);

        const readonly = computed(() => viewAction.value || deleteAction.value || printAction.value);

        const templateApplication = ref({});

        function selectItem(item) {
            templateApplication.value = JSON.parse(JSON.stringify(item));
        }

        function ajouter() {
            templateApplication.value = {
                module: props.module,
                moduleId: $route.params.id
            }
            addAction.value = true;
            dialog.value = true;
        }

        function modifier(item) {
            selectItem(item);
            updateAction.value = true;
            dialog.value = true;
        }

        function supprimer(item) {
            selectItem(item);
            deleteAction.value = true;
            dialog.value = true;
        }

        function view(item) {
            selectItem(item);
            viewAction.value = true;
            dialog.value = true;
        }

        function print(item) {
            selectItem(item);
            printAction.value = true;
            dialog.value = true;
        }

        function closeDialog() {
            dialog.value = false;
            addAction.value = false;
            updateAction.value = false;
            deleteAction.value = false;
            viewAction.value = false;
            printAction.value = false;
            overlay.value = false;
            $refs.form.reset();
        }

        function generate() {
            if (templateApplication.value.templateId) {
                loading.value = true;
                axios.post(`/templateApplications/generate`, templateApplication.value).then((response) => {
                    templateApplication.value = response.data;
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/templateApplications', templateApplication.value).then((response) => {
                    templateApplicationStore.addTemplateApplication(response.data);
                    closeDialog();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/templateApplications', templateApplication.value).then((response) => {
                    templateApplicationStore.updateTemplateApplication(response.data);
                    closeDialog();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/templateApplications/${templateApplication.value.id}`).then(() => {
                templateApplicationStore.deleteTemplateApplication(templateApplication.value);
                closeDialog();
            }).finally(() => {
                loading.value = false;
            });
        }

        return {
            mxRules,
            templateApplicationStore,
            templateApplication,
            filter,
            total,
            itemsPerPage,
            headers,
            actions,
            searchParams,
            dialog,
            overlay,
            loading,
            listingLoading,
            addAction,
            updateAction,
            deleteAction,
            viewAction,
            printAction,
            readonly,
            findAll,
            closeDialog,
            ajouter,
            modifier,
            supprimer,
            view,
            generate,
            save,
            update,
            remove,
            print,
            getBack,
            itemClick,
            rowColor
        }
    }
}
</script>

<template>
    <div>
        <v-card>
            <w-app
                :title="$t('module.template_application')"
                :total="total"
            >
                <template #rightToolbar>
                    <w-btn-reload
                        :loading="loading"
                        @press="findAll"
                    />
                    <w-btn-add
                        show
                        @press="ajouter"
                    />
                    <w-btn-close @press="getBack"/>
                </template>

                <template #content>
                    <w-server-data-table
                        v-model="searchParams"
                        :actions="actions"
                        :headers="headers"
                        :items="templateApplicationStore.templateApplications"
                        :loading="listingLoading"
                        :total="total"
                        :module="module"
                        fixed-header
                        :height="module ? '600' : null"
                        @rowDbClick="view"
                    >
                        <template v-slot:[`item.name`]="{ item }">
                            <w-inline-text bold :text="item.name"/>
                        </template>
                    </w-server-data-table>
                </template>
            </w-app>
        </v-card>

        <v-dialog
            v-model="dialog"
            persistent
            scrollable
            max-width="900"
        >
            <v-card>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                    :print-action="printAction"
                />
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-form lazy-validation ref="form">
                        <v-container fluid>
                            <w-fieldset :title="$t('common.details')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="templateApplication.name"
                                            auto-focus
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('module.template')" required/>
                                        <str-template-autocomplete
                                            v-model="templateApplication.templateId"
                                            type="CONTENT"
                                            :module="templateApplication.module"
                                            :readonly="readonly"
                                        />
                                    </v-col>

                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('module.template')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <str-quill-editor-core-pdf
                                            v-if="readonly"
                                            v-model="templateApplication.contentApplication"
                                        />
                                        <str-quill-editor
                                            v-else
                                            v-model="templateApplication.contentApplication"
                                            :disabled="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="closeDialog"/>
                    <w-btn-print
                        :show="printAction"
                    />
                    <w-btn
                        v-if="!readonly"
                        icon="mdi-blender"
                        color="purple"
                        :text="$t('common.generate')"
                        :loading="loading"
                        @press="generate"
                    />
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>